import React, { lazy } from 'react'
import { Route, useNavigate, useParams } from 'react-router-dom'
import { Provider as JotaiProvider } from 'jotai'
import {
  shotListStore,
  editorCommentStore,
  videoEditorStore
} from '../AppJotaiStores'
import showPaywall from '../services/show-paywall'
import { currentRouteFeature } from '../services/feature-from-route'
import { AuthContext } from '../context/firebase-context'
import useFeatureItemAccess from '../modules/Projects/components/FeatureItems/useFeatureItemAccess'
import { canAccessSceneWorkflow } from '../modules/Storyboard/sceneWorkflowAccess'
import { canAccessVideoEditor } from '../common/video-editor-access'
import { lazyWithPreload } from './lazy-with-preload'
import { FeatureBoundary } from '../components/core/ErrorBoundary'

const BreakSmart = lazy(() => import('../modules/SceneBreakdown'))
const Storyboard = lazy(() => import('../modules/Storyboard'))
const ShotList = lazy(() => import('../modules/ShotList'))
// Exported for `route-preload`: a deep link from brand lands here signed in,
// so the chunk is worth starting alongside the sign-in rather than after it.
export const Editor = lazyWithPreload(() => import('../modules/NewEditor'))
const VideoEditor = lazy(() => import('../modules/VideoEditor'))
const Scheduling = lazy(() => import('../modules/Scheduling'))
const PitchDeck = lazy(() => import('../modules/PitchDeck'))
const PlotHole = lazy(() => import('../modules/PlotHole'))
const ScriptCoverage = lazy(() => import('../modules/ScriptCoverage'))
const ScriptReport = lazy(() => import('../modules/ScriptCoverage/containers/ScriptCoverageReportContainer'))
const Budgeting = lazy(() => import('../modules/Budgeting'))
const BudgetWizard = lazy(() => import('../modules/Budgeting/containers/BudgetingWizardContainer'))
const Backstage = lazy(() => import('../modules/backstage/backstage-container'))
const CreateTreatment = lazy(() => import('../modules/Treatment'))
const CreatePilot = lazy(() => import('../modules/PilotTreatment'))
const BoxOffice = lazy(() => import('../modules/BoxOffice/containers/MainContainer'))
const BoxOfficeForecastCreate = lazy(() => import('../modules/BoxOffice/containers/CreateContainer'))
const BoxOfficeWorkflow = lazy(() => import('../modules/BoxOffice/containers/WorkflowContainer'))
const SceneWorkflow = lazy(() => import('../modules/Storyboard/containers/SceneWorkflowContainer'))

// Route-level access guard for per-script pages
// Leaving a route the user cannot enter should put them back where they came
// from, not on the dashboard: a click from the project's own nav that ends on
// /dashboard reads as the app losing their place. Returns the blocked path so
// the caller can report it as `targetPath` -- the impression fires after the
// navigation has landed, so `path` records where they ended up and only
// `target_path` can say what they were actually denied.
const leaveBlockedRoute = (navigate) => {
  // eslint-disable-next-line no-undef
  const blockedPath = typeof window !== 'undefined' ? window.location?.pathname : null
  // eslint-disable-next-line no-undef
  const hasHistory = typeof window !== 'undefined' && window.history?.length > 1
  if (hasHistory) {
    navigate(-1)
  } else {
    navigate('/dashboard')
  }
  return blockedPath
}

export const AccessGuard = ({ children, requiresPro = false, requiresPaidPlan = false }) => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const user = React.useContext(AuthContext)
  const { canAccess, knowsScript, refreshScripts, scriptsPending, workspacePending, activeWorkspace } = useFeatureItemAccess()
  const shownRef = React.useRef(false)
  // Keyed by slug so each script gets one re-read and a real block cannot loop.
  const refetchedRef = React.useRef(null)
  const blockedByProPlan = requiresPro && !workspacePending && !canAccessSceneWorkflow(user, { activeWorkspace })
  // Paid-plan surfaces (the AI Video editor) accept any subscription, so this
  // is deliberately a separate check from `requiresPro` above rather than a
  // tier comparison — the two gates answer different questions.
  const blockedByFreePlan = requiresPaidPlan && !workspacePending && !canAccessVideoEditor({ user, activeWorkspace })

  React.useEffect(() => {
    if (workspacePending) return
    if (scriptsPending) return

    // A script created since the list was fetched is missing from it, which
    // canAccess reports as denied. Re-read once first: unknown is not denied.
    if (slug && !canAccess(slug) && !knowsScript(slug) && refetchedRef.current !== slug) {
      refetchedRef.current = slug
      refreshScripts()
      return
    }

    if (slug && blockedByProPlan && !shownRef.current) {
      shownRef.current = true
      try {
        // Resolve the blocked route's feature BEFORE navigating away: the
        // impression fires after the redirect has landed, so by then the
        // resolver only sees /dashboard and the gate records no feature at
        // all.
        const featureName = currentRouteFeature()
        const targetPath = leaveBlockedRoute(navigate)
        showPaywall('pro', { paywallType: 'feature', featureName, trigger: 'route_guard', targetPath })
      } catch (e) {
        console.error(e)
      }
      return
    }

    if (slug && blockedByFreePlan && !shownRef.current) {
      shownRef.current = true
      try {
        // Named outright rather than resolved from the route: `requiresPaidPlan`
        // guards the two /video-editor routes and nothing else, so the feature
        // is known here, and the resolver would answer for /dashboard once the
        // redirect below has landed.
        const targetPath = leaveBlockedRoute(navigate)
        showPaywall('creator', { paywallType: 'feature', featureName: 'aiVideo', trigger: 'route_guard', targetPath })
      } catch (e) {
        console.error(e)
      }
      return
    }

    if (slug && !canAccess(slug) && !shownRef.current) {
      shownRef.current = true
      try {
        const featureName = currentRouteFeature()
        const targetPath = leaveBlockedRoute(navigate)
        showPaywall('creator', { paywallType: 'feature', featureName, trigger: 'route_guard', targetPath })
      } catch (e) {
        console.error(e)
      }
    }
  }, [slug, canAccess, knowsScript, refreshScripts, navigate, blockedByProPlan, blockedByFreePlan, scriptsPending, workspacePending])

  if (!slug) return children
  if (workspacePending) return null
  if (scriptsPending) return null
  // Mid re-read: the answer is not known yet, so render nothing.
  if (slug && !canAccess(slug) && !knowsScript(slug) && refetchedRef.current !== slug) return null
  if (blockedByProPlan) return null
  if (blockedByFreePlan) return null
  if (!canAccess(slug)) return null
  return children
}

const guarded = (feature, node, guardProps = {}) => (
  <FeatureBoundary feature={feature}>
    <AccessGuard {...guardProps}>{node}</AccessGuard>
  </FeatureBoundary>
)

export const editorRoutes = [
  <Route key='break-smart' path='/editor/break-smart/:slug' element={guarded('breakdown', <BreakSmart />)} />,
  <Route key='break-smart-with-project' path='/editor/break-smart/:projectSlug/:slug' element={guarded('breakdown', <BreakSmart />)} />,
  <Route key='breakdown' path='/breakdown/:slug' element={guarded('breakdown', <BreakSmart />)} />,
  <Route key='breakdown-with-project' path='/breakdown/:projectSlug/:slug' element={guarded('breakdown', <BreakSmart />)} />,
  <Route key='storyboard' path='/editor/storyboard/:slug' element={guarded('storyboard', <Storyboard />)} />,
  <Route key='storyboard-with-project' path='/editor/storyboard/:projectSlug/:slug' element={guarded('storyboard', <Storyboard />)} />,
  <Route key='scene-workflow' path='/editor/storyboard/:slug/workflow' element={guarded('storyboard', <SceneWorkflow />, { requiresPro: true })} />,
  <Route key='scene-workflow-with-project' path='/editor/storyboard/:projectSlug/:slug/workflow' element={guarded('storyboard', <SceneWorkflow />, { requiresPro: true })} />,
  <Route
    key='shot-list'
    path='/shot-list/:slug'
    element={guarded('shotList', <JotaiProvider store={shotListStore}><ShotList /></JotaiProvider>)}
  />,
  <Route
    key='shot-list-with-project'
    path='/shot-list/:projectSlug/:slug'
    element={guarded('shotList', <JotaiProvider store={shotListStore}><ShotList /></JotaiProvider>)}
  />,
  <Route
    key='editor'
    path='/editor/:slug'
    element={guarded('editor', <JotaiProvider store={editorCommentStore}><Editor /></JotaiProvider>)}
  />,
  <Route
    key='editor-with-project'
    path='/editor/:projectSlug/:slug'
    element={guarded('editor', <JotaiProvider store={editorCommentStore}><Editor /></JotaiProvider>)}
  />,
  <Route
    key='video-editor'
    path='/video-editor/:slug'
    element={guarded('aiVideo', <JotaiProvider store={videoEditorStore}><VideoEditor /></JotaiProvider>, { requiresPaidPlan: true })}
  />,
  <Route
    key='video-editor-with-project'
    path='/video-editor/:projectSlug/:slug'
    element={guarded('aiVideo', <JotaiProvider store={videoEditorStore}><VideoEditor /></JotaiProvider>, { requiresPaidPlan: true })}
  />,
  <Route key='scheduling' path='/scheduling/:slug' element={guarded('scheduling', <Scheduling />)} />,
  <Route key='scheduling-with-project' path='/scheduling/:projectSlug/:slug' element={guarded('scheduling', <Scheduling />)} />,
  <Route key='pitch-deck' path='/pitch-deck/:slug' element={guarded('pitchDeck', <PitchDeck />)} />,
  <Route key='pitch-deck-with-project' path='/pitch-deck/:projectSlug/:slug' element={guarded('pitchDeck', <PitchDeck />)} />,
  <Route key='plot-hole' path='/plot-hole-report/:slug' element={guarded('plotHole', <PlotHole />)} />,
  <Route key='plot-hole-with-project' path='/plot-hole-report/:projectSlug/:slug' element={guarded('plotHole', <PlotHole />)} />,
  <Route key='script-coverage' path='/script-coverage/:slug' element={guarded('scriptCoverage', <ScriptCoverage />)} />,
  <Route key='script-coverage-with-project' path='/script-coverage/:projectSlug/:slug' element={guarded('scriptCoverage', <ScriptCoverage />)} />,
  <Route
    key='script-report'
    path='/script-coverage/:slug/report/:reportSlug'
    element={guarded('scriptCoverage', <ScriptReport />)}
  />,
  <Route
    key='script-report-with-project'
    path='/script-coverage/:projectSlug/:slug/report/:reportSlug'
    element={guarded('scriptCoverage', <ScriptReport />)}
  />,
  <Route key='budget-wizard' path='/budgeting-forecast/wizard/:slug' element={guarded('budgeting', <BudgetWizard />)} />,
  <Route key='budget-wizard-with-project' path='/budgeting-forecast/wizard/:projectSlug/:slug' element={guarded('budgeting', <BudgetWizard />)} />,
  <Route key='budgeting' path='/budgeting-forecast/:slug' element={guarded('budgeting', <Budgeting />)} />,
  <Route key='budgeting-with-project' path='/budgeting-forecast/:projectSlug/:slug' element={guarded('budgeting', <Budgeting />)} />,
  <Route key='backstage' path='/backstage' element={<Backstage />} />,
  <Route key='treatment' path='/create-script/treatment' element={<FeatureBoundary feature='projectCreation'><CreateTreatment /></FeatureBoundary>} />,
  <Route key='treatment-with-project' path='/create-script/:projectSlug/treatment' element={<FeatureBoundary feature='projectCreation'><CreateTreatment /></FeatureBoundary>} />,
  <Route key='pilot' path='/create-script/pilot/treatment' element={<FeatureBoundary feature='projectCreation'><CreatePilot /></FeatureBoundary>} />,
  <Route key='pilot-with-project' path='/create-script/:projectSlug/pilot/treatment' element={<FeatureBoundary feature='projectCreation'><CreatePilot /></FeatureBoundary>} />,
  <Route key='box-office' path='/box-office/:projectSlug/:slug' element={<AccessGuard><BoxOffice /></AccessGuard>} />,
  <Route key='box-office-create' path='/box-office/:projectSlug/:slug/create' element={<AccessGuard><BoxOfficeForecastCreate /></AccessGuard>} />,
  <Route key='box-office-workflow' path='/box-office/:projectSlug/:slug/workflow' element={<AccessGuard><BoxOfficeWorkflow /></AccessGuard>} />
]
